import React, { useState, useEffect } from 'react';
import { EmailMarketingForm } from '../types';
import { X, Gift } from 'lucide-react';
import EmailMarketingFormWidget from './EmailMarketingFormWidget';

interface EmailMarketingPopupModalProps {
  form: EmailMarketingForm;
  apiUrl?: string;
}

export const EmailMarketingPopupModal: React.FC<EmailMarketingPopupModalProps> = ({ form, apiUrl = '/api' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasDismissed, setHasDismissed] = useState(false);

  useEffect(() => {
    if (!form || !form.is_active || form.placement !== 'popup') return;

    // Check if dismissed in this session or within reappear hours
    const storageKey = `email_popup_closed_${form.id}`;
    const reappearHours = form.behavior_config?.reappearHours ?? 24;

    if (reappearHours > 0) {
      const closedAt = localStorage.getItem(storageKey);
      if (closedAt) {
        const diffHours = (Date.now() - parseInt(closedAt, 10)) / (1000 * 60 * 60);
        if (diffHours < reappearHours) return;
      }
    } else {
      if (sessionStorage.getItem(storageKey)) return;
    }

    let timerId: NodeJS.Timeout | null = null;

    // 1. Timer trigger (default 5s)
    const delaySeconds = form.behavior_config?.popupDelaySeconds ?? 5;
    if (delaySeconds >= 0) {
      timerId = setTimeout(() => {
        setIsOpen(true);
      }, delaySeconds * 1000);
    }

    // 2. Exit Intent trigger (when mouse moves out of window top)
    const handleMouseLeave = (e: MouseEvent) => {
      if (form.behavior_config?.exitIntent && e.clientY <= 15 && !hasDismissed) {
        setIsOpen(true);
      }
    };

    // 3. Scroll Percentage trigger
    const handleScroll = () => {
      if (hasDismissed) return;
      const scrollPercent = form.behavior_config?.scrollPercent ?? 0;
      if (scrollPercent > 0) {
        const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (totalHeight > 0) {
          const currentPercent = (window.scrollY / totalHeight) * 100;
          if (currentPercent >= scrollPercent) {
            setIsOpen(true);
          }
        }
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      if (timerId) clearTimeout(timerId);
      document.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [form, hasDismissed]);

  const handleClose = () => {
    setIsOpen(false);
    setHasDismissed(true);
    const storageKey = `email_popup_closed_${form.id}`;
    localStorage.setItem(storageKey, Date.now().toString());
    sessionStorage.setItem(storageKey, 'true');
  };

  if (!isOpen || hasDismissed) return null;

  return (
    <div 
      id={`email-popup-modal-${form.id}`}
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-md animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div className="relative w-full max-w-lg bg-white dark:bg-slate-900 rounded-[32px] shadow-[0_25px_70px_rgba(0,0,0,0.45)] overflow-hidden border border-slate-200 dark:border-slate-800 animate-fade-in-up">
        {/* Close Button */}
        <button
          id={`email-popup-close-btn-${form.id}`}
          onClick={handleClose}
          aria-label="Fechar pop-up"
          className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-500 hover:text-slate-800 dark:text-slate-300 flex items-center justify-center transition-all cursor-pointer shadow-sm"
        >
          <X size={18} />
        </button>

        <EmailMarketingFormWidget
          form={form}
          apiUrl={apiUrl}
          variant="modal"
          onSuccess={() => {
            setTimeout(() => {
              handleClose();
            }, 3000);
          }}
          className="border-none shadow-none !p-7 sm:!p-8"
        />
      </div>
    </div>
  );
};

export default EmailMarketingPopupModal;
